import React, { useEffect, useState } from 'react';
import { ShieldCheck, CheckCircle } from 'lucide-react';

// Pop-up on the driver and night-sorter portals asking a visa holder to consent to a
// VEVO check. Shown when getMyDms says so (a visa on file, no consent yet, and from
// the prompt start date). Consent must be freely given, so "Not now" always closes it —
// for this visit only; it comes back next time they open the portal until they consent.
// Their consent is recorded exactly as from the My Docs panel (setVevoConsent).

const API = '/.netlify/functions/driver-dms-sb';
const post = async (payload) => (await fetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) })).json();
const dismissKey = (login) => `hp-vevo-consent-later-${login || ''}`;

export default function VevoConsentPrompt({ login }) {
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    try { if (sessionStorage.getItem(dismissKey(login))) return undefined; } catch { /* storage blocked — still ask */ }
    post({ action: 'getMyDms' }).then((d) => {
      if (live && d && d.success && d.vevoConsentPrompt) { setText(d.vevoConsentText || ''); setOpen(true); }
    }).catch(() => {});
    return () => { live = false; };
  }, [login]);

  if (!open) return null;

  const later = () => {
    try { sessionStorage.setItem(dismissKey(login), '1'); } catch { /* ignore */ }
    setOpen(false);
  };
  const consent = async () => {
    setBusy(true); setError('');
    try {
      const d = await post({ action: 'setVevoConsent', given: true });
      if (d && d.success) { setDone(true); setTimeout(() => setOpen(false), 2200); }
      else setError((d && d.error) || 'Could not save — please try again, or use My Docs.');
    } catch (e) { setError('Could not save — check your connection and try again.'); }
    finally { setBusy(false); }
  };

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="vevo-consent-title"
      style={{ position: 'fixed', inset: 0, zIndex: 5000, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ width: 'min(440px, 100%)', maxHeight: '90vh', overflowY: 'auto', background: 'var(--hp-card, var(--hp-bg, #fff))', color: 'var(--hp-text)', border: '1px solid var(--hp-border)', borderRadius: 14, padding: '20px 18px', boxShadow: '0 12px 40px rgba(0,0,0,0.35)' }}>
        {done ? (
          <div style={{ textAlign: 'center', padding: '10px 0' }}>
            <CheckCircle size={40} color="var(--hp-green, #16a34a)" />
            <div style={{ fontWeight: 800, fontSize: '1.05rem', marginTop: 10 }}>Thank you — consent saved</div>
            <div style={{ fontSize: '0.85rem', color: 'var(--hp-dim)', marginTop: 6 }}>You can withdraw it any time in My Docs.</div>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <ShieldCheck size={26} color="var(--hp-gold)" />
              <h2 id="vevo-consent-title" style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800 }}>Visa check consent</h2>
            </div>
            <p style={{ fontSize: '0.9rem', lineHeight: 1.5, margin: '0 0 12px' }}>
              Hollypaul must confirm the work rights of everyone who works here on a visa. We do this with
              the Department of Home Affairs (VEVO), and we need your OK first.
            </p>
            <div style={{ fontSize: '0.86rem', lineHeight: 1.5, padding: '10px 12px', borderRadius: 10, background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', marginBottom: 14 }}>
              {text}
            </div>
            {error && <div style={{ color: 'var(--hp-red, #dc2626)', fontSize: '0.84rem', marginBottom: 10 }}>{error}</div>}
            <button onClick={consent} disabled={busy}
              style={{ width: '100%', padding: '14px 16px', border: 'none', borderRadius: 10, background: 'var(--hp-gold)', color: '#1a1a2e', fontWeight: 800, fontSize: '1rem', cursor: 'pointer', opacity: busy ? 0.7 : 1 }}>
              {busy ? 'Saving…' : 'I consent'}
            </button>
            <button onClick={later} disabled={busy}
              style={{ width: '100%', marginTop: 8, padding: '11px 16px', border: '1px solid var(--hp-border)', borderRadius: 10, background: 'none', color: 'var(--hp-dim)', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer' }}>
              Not now
            </button>
          </>
        )}
      </div>
    </div>
  );
}
